import { useState, useEffect } from 'react';
import { Trash2, ImageIcon } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent } from '../../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TombstoneQuote, TombstoneProduct } from '../../../types/tombstones';
import { createQuote, fetchProducts } from '../../../services/tombstones.service';
import { formatCurrency } from './tombstoneQuoteHelpers';

interface CreateQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken?: string;
  onQuoteCreated: (quote: TombstoneQuote) => void;
}

interface QuoteItemForm {
  id: string;
  productId: string;
  quantity: number;
  dimensionId?: string;
  dimensionLabel?: string;
  // For display
  productName: string;
  productCode?: string;
  productImageUrl?: string;
  productPrice: number;
}

interface CreateQuoteFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerCompany: string;
  customerDocument: string;
  customerSegment: string;
  notes: string;
}

export function CreateQuoteDialog({
  open,
  onOpenChange,
  accessToken,
  onQuoteCreated
}: CreateQuoteDialogProps) {
  const [formData, setFormData] = useState<CreateQuoteFormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCompany: '',
    customerDocument: '',
    customerSegment: '',
    notes: ''
  });

  const [items, setItems] = useState<QuoteItemForm[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reference data
  const [products, setProducts] = useState<TombstoneProduct[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Add product form state
  const [showAddProduct, setShowAddProduct] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedDimensionId, setSelectedDimensionId] = useState('');
  const [addQuantity, setAddQuantity] = useState(1);

  // Load products when dialog opens
  useEffect(() => {
    if (open && accessToken) {
      loadProducts();
    }
  }, [open, accessToken]);

  const loadProducts = async () => {
    if (!accessToken) return;

    setIsLoadingData(true);
    setError(null);
    try {
      const productsData = await fetchProducts(accessToken, { includeInactive: false });
      setProducts(productsData.filter(p => p.active));
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Erro ao carregar produtos');
    } finally {
      setIsLoadingData(false);
    }
  };

  const selectedProduct = products.find(p => p.id === selectedProductId);
  const selectedDimension = selectedProduct?.dimensions?.find(d => d.id === selectedDimensionId);

  const handleAddItem = () => {
    if (!selectedProductId) {
      setError('Selecione um produto');
      return;
    }

    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;

    // If product has dimensions, require dimension selection
    if (product.dimensions && product.dimensions.length > 0 && !selectedDimensionId) {
      setError('Selecione uma dimensão');
      return;
    }

    if (addQuantity < 1) {
      setError('Quantidade deve ser maior que zero');
      return;
    }

    // Determine price and dimension info
    let unitPrice = product.price;
    let dimensionLabel: string | undefined;

    if (selectedDimension) {
      unitPrice = selectedDimension.price;
      dimensionLabel = selectedDimension.label;
    }

    const newItem: QuoteItemForm = {
      id: `item-${Date.now()}-${Math.random()}`,
      productId: selectedProductId,
      quantity: addQuantity,
      dimensionId: selectedDimensionId || undefined,
      dimensionLabel: dimensionLabel,
      productName: product.name,
      productCode: product.code,
      productImageUrl: product.imageUrl,
      productPrice: unitPrice
    };

    setItems([...items, newItem]);

    // Reset form
    setSelectedProductId('');
    setSelectedDimensionId('');
    setAddQuantity(1);
    setError(null);
  };

  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accessToken) {
      setError('Não autenticado. Faça login novamente.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const requestBody: {
        customerName?: string;
        customerEmail?: string;
        customerPhone?: string;
        customerCompany?: string;
        customerDocument?: string;
        customerSegment?: string;
        notes?: string;
        items?: Array<{
          productId: string;
          quantity: number;
        }>;
      } = {};

      if (formData.customerName.trim()) requestBody.customerName = formData.customerName.trim();
      if (formData.customerEmail.trim()) requestBody.customerEmail = formData.customerEmail.trim();
      if (formData.customerPhone.trim()) requestBody.customerPhone = formData.customerPhone.trim();
      if (formData.customerCompany.trim()) requestBody.customerCompany = formData.customerCompany.trim();
      if (formData.customerDocument.trim()) requestBody.customerDocument = formData.customerDocument.trim();
      if (formData.customerSegment.trim()) requestBody.customerSegment = formData.customerSegment.trim();
      if (formData.notes.trim()) requestBody.notes = formData.notes.trim();

      if (items.length > 0) {
        requestBody.items = items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          dimensionId: item.dimensionId || undefined
        }));
      }

      const newQuote = await createQuote(requestBody, accessToken);

      // Reset form
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerCompany: '',
        customerDocument: '',
        customerSegment: '',
        notes: ''
      });
      setItems([]);
      setSelectedProductId('');
      setSelectedDimensionId('');
      setAddQuantity(1);

      onQuoteCreated(newQuote);
    } catch (err) {
      console.error('Error creating quote:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido ao criar orçamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerCompany: '',
      customerDocument: '',
      customerSegment: '',
      notes: ''
    });
    setItems([]);
    setSelectedProductId('');
    setSelectedDimensionId('');
    setAddQuantity(1);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Novo Orçamento de Tombstone</DialogTitle>
          <DialogDescription>
            Preencha os dados do cliente e adicione os produtos ao orçamento.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Information Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold text-sm">Dados do Cliente (Opcional)</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="customerName">Nome do Cliente</Label>
                <Input
                  id="customerName"
                  type="text"
                  placeholder="Nome completo (opcional)"
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerEmail">Email</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  placeholder="email@exemplo.com (opcional)"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerPhone">Telefone</Label>
                <Input
                  id="customerPhone"
                  type="tel"
                  placeholder="(00) 00000-0000 (opcional)"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerCompany">Empresa</Label>
                <Input
                  id="customerCompany"
                  type="text"
                  placeholder="Nome da empresa (opcional)"
                  value={formData.customerCompany}
                  onChange={(e) => setFormData({ ...formData, customerCompany: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerDocument">CPF/CNPJ</Label>
                <Input
                  id="customerDocument"
                  type="text"
                  placeholder="000.000.000-00 (opcional)"
                  value={formData.customerDocument}
                  onChange={(e) => setFormData({ ...formData, customerDocument: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerSegment">Segmento</Label>
                <Input
                  id="customerSegment"
                  type="text"
                  placeholder="Segmento de atuação (opcional)"
                  value={formData.customerSegment}
                  onChange={(e) => setFormData({ ...formData, customerSegment: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                placeholder="Observações ou detalhes adicionais (opcional)"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          {/* Items Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <h3 className="font-semibold text-sm">Itens do Orçamento</h3>

            {/* Added Items List */}
            {items.length > 0 && (
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                    {item.productImageUrl ? (
                      <img
                        src={item.productImageUrl}
                        alt={item.productName}
                        className="h-10 w-10 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.productName}
                        {item.dimensionLabel && (
                          <span className="text-xs text-muted-foreground ml-1">({item.dimensionLabel}cm)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.quantity}x {formatCurrency(item.productPrice)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Product Form */}
            <Card className="border-2 border-dashed">
              <CardContent className="p-4 space-y-3">
                <Label className="text-sm font-medium">Adicionar Produto</Label>

                {/* Product List */}
                <div>
                  <Label className="text-sm text-muted-foreground">Produto</Label>
                  <div className="mt-1 max-h-48 overflow-y-auto rounded-md border">
                    {isLoadingData ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Carregando produtos...
                      </div>
                    ) : products.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Nenhum produto disponível
                      </div>
                    ) : (
                      products.map((product) => (
                        <div
                          key={product.id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0 ${
                            selectedProductId === product.id ? 'bg-accent ring-1 ring-primary' : ''
                          }`}
                          onClick={() => {
                            setSelectedProductId(product.id);
                            setSelectedDimensionId('');
                          }}
                        >
                          {product.imageUrl ? (
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              className="h-10 w-10 rounded object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                              <ImageIcon className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{product.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {product.dimensions && product.dimensions.length > 0
                                ? `${product.dimensions.length} tamanho(s)`
                                : formatCurrency(product.price)}
                            </p>
                          </div>
                          {product.description && (
                            <p className="text-xs text-muted-foreground text-right max-w-[40%] line-clamp-2 flex-shrink-0">
                              {product.description}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Dimension selection (shown when product with dimensions is selected) */}
                {selectedProductId && selectedProduct && selectedProduct.dimensions && selectedProduct.dimensions.length > 0 && (
                  <div className="space-y-3 pt-2 border-t">
                    <div>
                      <Label htmlFor="dimension" className="text-sm">Dimensão</Label>
                      <Select value={selectedDimensionId} onValueChange={setSelectedDimensionId}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecione a dimensão" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedProduct.dimensions.map((dim) => (
                            <SelectItem key={dim.id} value={dim.id}>
                              {dim.label} ({dim.widthCm}x{dim.heightCm}cm) - {formatCurrency(dim.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Quantity (shown when product is selected) */}
                {selectedProductId && (
                  <div className="space-y-3 pt-2 border-t">
                    {/* Quantity */}
                    <div>
                      <Label htmlFor="quantity" className="text-sm">Quantidade</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={addQuantity}
                        onChange={(e) => setAddQuantity(parseInt(e.target.value) || 1)}
                        className="mt-1"
                      />
                    </div>

                    {/* Add Button */}
                    <Button
                      type="button"
                      onClick={handleAddItem}
                      disabled={!selectedProductId || (selectedProduct?.dimensions && selectedProduct.dimensions.length > 0 && !selectedDimensionId)}
                      className="w-full"
                    >
                      Adicionar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {items.length === 0 && !selectedProductId && (
              <div className="text-sm text-muted-foreground text-center py-2">
                Selecione um produto acima para adicionar ao orçamento.
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Criando...' : 'Criar Orçamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
